/* eslint-disable no-confusing-arrow */
import dayjs from 'dayjs';
import sign from 'jwt-encode';
import { useLocation } from 'react-router-dom';
import { addDays, subDays } from 'date-fns';
import WBCONST from '../constants/wb.const';
import PATH from '../constants/path.const';
import { getColors } from '../../theme';

const colors = getColors();

const { binColor } = colors;
const {
  openTasks,
  developmentCompleted,
  completedTasks,
  tasksToReview,
  groupStatus,
  bugTrackerAuthorizedTeam,
  qaSubTeams,
  ROBOTEON_APP_ISSUES,
  AI4WMS,
  STS,
  SYSTEM_PREP,
} = WBCONST;

export const encodePassword = (str) => sign(str, process.env.REACT_APP_PASS_SECRET);

// Maps QA sub-teams (QA-MANUAL, QA-AUTO, ...) back to the single 'QA' team
// name so team-based access checks, filters, and icons keep working after
// the backend split the QA team.
export const normalizeTeamName = (teamName) =>
  qaSubTeams.includes(teamName) ? bugTrackerAuthorizedTeam : teamName;

export function splitStringIntoTwoParts(str) {
  const words = str.split(' ');
  const lastWord = words.pop();
  const firstPart = words.join(' ');
  return [firstPart, lastWord];
}

export function useQueryParams() {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const params = Object.fromEntries(urlSearchParams.entries());
  return params;
}

export const dateFormatter = (values, format = 'DD MMM, YYYY h:mm A') =>
  dayjs(values).format(format);
export const dateTimeFormatter = (values) => dayjs(values).format('YYYY-MM-DD HH:mm:ss');
export const dateFormatter2 = (values, format = 'MM/DD/YYYY HH:mm') => dayjs(values).format(format);
export const dateFormatter3 = (values, format = 'DD MMM YYYY') => dayjs(values).format(format);

export const valueSearchFilter = (data, searchQuery) =>
  data.filter((item) =>
    Object.values(item).some((value) =>
      value?.toString()?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  );

export const keySearchFilter = (data, searchQuery, key) =>
  data.filter((item) => {
    const value = key.split('.').reduce((obj, k) => obj?.[k], item);
    return value?.toString().toLowerCase().includes(searchQuery?.toLowerCase());
  });

export const createBinColorMap = (cart) => {
  const color = binColor;
  const obj = { none: binColor };
  cart[0].binInfo.forEach((bin) => {
    obj[bin.binId] = color;
  });
  return obj;
};

export const createBinTransparencyMap = (cart) => {
  const transaparency = 1;
  const obj = { none: 1 };
  cart[0].binInfo.forEach((bin) => {
    obj[bin.binId] = transaparency;
  });
  return obj;
};

const separateDynamicSegments = (path) => {
  const segments = path.split('/');
  const staticSegments = [];
  const dynamicSegments = [];

  segments.forEach((segment) => {
    if (segment.startsWith(':')) {
      dynamicSegments.push(segment);
    } else {
      staticSegments.push(segment);
    }
  });

  return {
    staticPath: staticSegments.join('/'),
    dynamicSegments: dynamicSegments.join('/'),
  };
};

const separateQueryParams = (url) => {
  const [path, queryParams] = url.split('?');
  return [path, queryParams ? `?${queryParams}` : ''];
};

const calculateShift = () =>
  localStorage
    .getItem('uuid')
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0) % 26;

export const maskPath = (path) => {
  if (localStorage.getItem('maskPath') === 'true') {
    const shift = calculateShift();
    const { staticPath, dynamicSegments } = separateDynamicSegments(path);
    const [basePath, queryParams] = separateQueryParams(staticPath);
    const maskBasePath = basePath
      .split('')
      .map((char) => {
        if (char >= 'A' && char <= 'Z') {
          return String.fromCharCode(((char.charCodeAt(0) - 65 + shift) % 26) + 65);
        }
        if (char >= 'a' && char <= 'z') {
          return String.fromCharCode(((char.charCodeAt(0) - 97 + shift) % 26) + 97);
        }
        return char;
      })
      .join('');
    return maskBasePath + queryParams + (dynamicSegments ? `/${dynamicSegments}` : '');
  }
  return path;
};

export const unMaskPath = (path) => {
  if (localStorage.getItem('maskPath') === 'true') {
    const shift = calculateShift();
    const { staticPath, dynamicSegments } = separateDynamicSegments(path);
    const [basePath, queryParams] = separateQueryParams(staticPath);
    const unMaskBasePath = basePath
      .split('')
      .map((char) => {
        if (char >= 'A' && char <= 'Z') {
          return String.fromCharCode(((char.charCodeAt(0) - 65 - shift + 26) % 26) + 65);
        }
        if (char >= 'a' && char <= 'z') {
          return String.fromCharCode(((char.charCodeAt(0) - 97 - shift + 26) % 26) + 97);
        }
        return char;
      })
      .join('');
    return unMaskBasePath + queryParams + (dynamicSegments ? `/${dynamicSegments}` : '');
  }
  return path;
};

export const maxPropertyLength = (properties) => {
  let max = 0;
  for (let index = 0; index < Object.keys(properties).length; index += 1) {
    const propertyKey = Object.keys(properties)[index];
    const property = properties[propertyKey];
    if (property && `${propertyKey}:\t${property}`.length > max) {
      max = `${propertyKey}:\t${property}`.length;
    }
  }
  return max;
};

export const findNearest = (value) => {
  if (value <= 10) {
    return 10;
  }
  if (value > 10 && value <= 50) {
    return 50;
  }
  if (value > 50 && value <= 500) {
    return Math.ceil(value / 50) * 50;
  }
  if (value > 500 && value <= 1000) {
    return Math.ceil(value / 100) * 100;
  }
  if (value > 1000) {
    const lastThreeDigits = value % 1000;
    if (
      (lastThreeDigits < 250 && lastThreeDigits < 500) ||
      (lastThreeDigits < 750 && lastThreeDigits > 500)
    ) {
      return Math.ceil(value / 100) * 100;
    }
    if (
      (lastThreeDigits >= 250 && lastThreeDigits <= 500) ||
      lastThreeDigits <= 500 ||
      lastThreeDigits >= 750
    ) {
      return Math.ceil(value / 500) * 500;
    }
  }
  return 10;
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

export const validateFileExtension = (f, extension) => {
  if (!f || !extension) return false;
  const fileExtension = f.name.split('.').pop().toLowerCase();
  const allowedExtensions = extension
    .toLowerCase()
    .split(',')
    .map((ext) => ext.trim().replace('.', ''));
  return allowedExtensions.includes(fileExtension);
};

export const encrypt = (inputText) =>
  inputText
    ?.split('')
    ?.map((char, index) =>
      String.fromCharCode(
        char.charCodeAt(0) +
          process.env.REACT_APP_PASS_SECRET.charCodeAt(
            index % process.env.REACT_APP_PASS_SECRET.length
          )
      )
    )
    .join('');

export const decrypt = (encryptedText) =>
  encryptedText
    ?.split('')
    ?.map((char, index) =>
      String.fromCharCode(
        char.charCodeAt(0) -
          process.env.REACT_APP_PASS_SECRET.charCodeAt(
            index % process.env.REACT_APP_PASS_SECRET.length
          )
      )
    )
    .join('');

export const getUserRole = () => {
  const encryptedRole = localStorage.getItem('role');
  const role = decrypt(encryptedRole);
  return role;
};

export const getDefaultStatus = () => 'Created';

export const getAdditionalPayload = () => {
  const type = localStorage.getItem('userType');
  return { taskType: type };
};

export const getDefaultCategory = (project, isBugs) => {
  const projects = ['STS', 'automate', 'ai4wms'];
  if (isBugs) {
    return 'Bug';
  }
  return projects.includes(project) ? 'Feature Request' : '';
};

export const getGroupStatus = (statuses, isBugs) => {
  if (isBugs) {
    return statuses?.map((status) =>
      status?.toLowerCase()?.includes('task') ? status.replace(/\bTasks\b/i, 'Bugs') : status
    );
  }
  return statuses;
};

export const getFormattedName = (name, project, isBugs, isClient) => {
  if (!name || typeof name !== 'string') return name;

  const isClientOrExternal = isClient || ![ROBOTEON_APP_ISSUES, STS, AI4WMS].includes(project);

  return name.replace(/\bTasks?\b/gi, (match) => {
    const isPlural = match.toLowerCase().endsWith('s');
    const isUpper = match === match.toUpperCase();
    const isCapital = match.charAt(0) === match.charAt(0).toUpperCase();

    if (isBugs) {
      if (isPlural) {
        if (isUpper) return 'BUGS';
        if (isCapital) return 'Bugs';
        return 'bugs';
      }
      if (isUpper) return 'BUG';
      if (isCapital) return 'Bug';
      return 'bug';
    }

    if (isClientOrExternal) {
      if (isPlural) {
        if (isUpper) return 'TICKETS';
        if (isCapital) return 'Tickets';
        return 'tickets';
      }
      if (isUpper) return 'TICKET';
      if (isCapital) return 'Ticket';
      return 'ticket';
    }

    return match;
  });
};

export const getColumns = (cols, isBugs) => {
  if (isBugs) {
    return cols?.map((col) =>
      col?.header?.toLowerCase()?.includes('task')
        ? {
            ...col,
            header: col.header.replace(/\bTask\b/i, 'Bug'),
            headerName: col.headerName.replace(/\bTask\b/i, 'Bug'),
          }
        : col
    );
  }
  return cols;
};

export const getFilesArr = (files) => {
  const filesArr = [];
  files.forEach((fil) => {
    const fileExtension = fil.fileName.split('.').pop().toLowerCase();
    let mimeType;
    switch (fileExtension) {
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      case 'xlsx':
      case 'xls':
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'png':
        mimeType = 'image/png';
        break;
      case 'jpg':
      case 'jpeg':
        mimeType = 'image/jpeg';
        break;
      case 'gif':
        mimeType = 'image/gif';
        break;
      case 'txt':
        mimeType = 'text/plain';
        break;
      case 'docx':
        mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        break;
      case 'pptx':
        mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        break;
      default:
        mimeType = 'application/octet-stream';
    }

    const obj = {
      name: fil.fileName,
      type: fileExtension,
      url: `data:${mimeType};base64,${fil.encodedString}`,
      data: fil.encodedString,
      mimeType,
    };
    filesArr.push(obj);
  });
  return filesArr;
};

export const getDateRange = (days) => {
  const endDate = new Date();
  const startDate = subDays(endDate, days - 1);
  return {
    startDate,
    endDate,
  };
};

export const getNextDateRange = (days) => {
  const startDate = new Date();
  const endDate = addDays(startDate, days - 1);
  return {
    startDate,
    endDate,
  };
};

export const getPrevDateRange = (days) => {
  const date = new Date();
  const endDate = date.setDate(date.getDate() - 1);
  const startDate = subDays(endDate, days - 1);
  return {
    startDate,
    endDate,
  };
};

export const getCurrentWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();

  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - dayOfWeek);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return { weekStart, weekEnd };
};

export const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const formatToDateTimeString = (dat, key) => {
  const date = new Date(dat).toLocaleDateString('en-CA');
  if (key === 'from') {
    return `${date}T00:00:00`;
  }
  if (key === 'to') {
    return `${date}T23:59:59`;
  }
  return date;
};

const colorMap = {
  'VERY HIGH': '#FF9999',
  HIGH: '#FFB266',
  MEDIUM: '#FFC300',
  LOW: '#B2E6B8',
  'VERY LOW': '#99CCF3',
  ReOpen: '#FF9999',
  Created: '#99CCF3',
  Backlog: '#F5A8AC',
  Assigned: '#FFD08A',
  'Work in Progress': '#FFC83D',
  Testing: '#FFC83D',
  'On Hold': '#8FA3BF',
  Cancelled: '#C4C4C4',
  Fixed: '#3DA754',
  Closed: '#C4C4C4',
  'Clarification Required': '#F6DC7B',
  Completed: '#3DA754',
  'Completed Tasks': '#3DA754',
};

export const getColor = (val) => {
  if (colorMap[val]) {
    return colorMap[val];
  }

  if (typeof val === 'number') {
    if (val === 100) return '#4FAF67';
    if (val > 75) return '#AFCB8E';
    if (val > 50) return '#E2D79A';
    if (val > 25) return '#FFD08A';
    return '#F8B4B8';
  }

  return '#F8B4B8';
};

export const getAvatarColor = (name, isDark) => {
  const hash = [...name].reduce((acc, char) => char.charCodeAt(0) + (acc * 31 - acc), 0);
  const r = (Math.abs(hash) % 100) + (isDark ? 80 : 180);
  const g = (Math.abs(hash / 2) % 100) + (isDark ? 80 : 180);
  const b = (Math.abs(hash / 3) % 100) + (isDark ? 80 : 180);
  return `rgb(${r}, ${g}, ${b})`;
};

export const filterStyle = {
  border: `1px solid ${colors.colorShades.lowLightGray}`,
  backgroundColor: colors.colorShades.lightGrey,
  borderRadius: '5px',
  px: 1,
  py: 0.5,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 1,
};

export const formatDate = (date) => date.toLocaleDateString('en-CA');
export const getUserById = (userId, userList) =>
  userList.find((user) => user.value === userId) || null;

export const getHashColor = (name, isDark) => {
  const hash = [...name].reduce((acc, char) => char.charCodeAt(0) + (acc * 31 - acc), 0);
  const r = (Math.abs(hash) % 100) + (isDark ? 80 : 180);
  const g = (Math.abs(hash / 2) % 100) + (isDark ? 80 : 180);
  const b = (Math.abs(hash / 3) % 100) + (isDark ? 80 : 180);
  return `rgb(${r}, ${g}, ${b})`;
};

export const getStatusTypes = (project) => ({
  open: openTasks,
  devCompleted: developmentCompleted,
  completed: completedTasks,
  review: tasksToReview,
});

export const groupByStatus = (tasks, isBugs) => {
  const groupedStatus = getGroupStatus(groupStatus, isBugs);
  const { open, devCompleted, completed, review } = getStatusTypes(isBugs);
  const groupedByStatus = {};
  groupedStatus.forEach((status) => {
    groupedByStatus[status] = [];
  });
  let i = 0;
  tasks.forEach((task) => {
    if (open.includes(task?.status)) {
      groupedByStatus[groupedStatus[0]].push({ ...task, id: i });
      i += 1;
    }
  });
  i = 0;
  tasks.forEach((task) => {
    if (devCompleted.includes(task?.status)) {
      groupedByStatus[groupedStatus[1]].push({ ...task, id: i });
      i += 1;
    }
  });
  i = 0;
  tasks.forEach((task) => {
    if (completed.includes(task?.status)) {
      groupedByStatus[groupedStatus[2]].push({ ...task, id: i });
      i += 1;
    }
  });
  i = 0;
  tasks.forEach((task) => {
    if (review.includes(task?.status)) {
      groupedByStatus[groupedStatus[3]].push({ ...task, id: i });
      i += 1;
    }
  });
  return groupedByStatus;
};

export const groupByTeams = (tasks, teamsList) => {
  const groupedByTeams = {};
  teamsList.forEach((team) => {
    groupedByTeams[team] = [];
  });
  tasks.forEach((task, index) => {
    task?.assigneeGroup?.forEach((team) => {
      groupedByTeams[normalizeTeamName(team)]?.push({ ...task, id: index });
    });
  });
  return groupedByTeams;
};

export const groupByCategories = (tasks, categoryList) => {
  const groupedByCategories = {};
  categoryList.forEach((category) => {
    groupedByCategories[category?.label] = [];
  });
  tasks.forEach((task, index) => {
    groupedByCategories[task?.category]?.push({ ...task, id: index });
  });
  return groupedByCategories;
};

export const groupBySystemPrep = (tasks) => {
  const systemPrepArray = [];
  tasks.forEach((task, index) => {
    if (task?.category === SYSTEM_PREP) {
      systemPrepArray.push({ ...task, id: index });
    }
  });
  return systemPrepArray;
};

export const groupByPriority = (tasks, priorityList) => {
  const groupedByPriority = {};
  priorityList.forEach((priority) => {
    groupedByPriority[priority] = [];
  });
  tasks.forEach((task, index) => {
    groupedByPriority[task?.priority]?.push({ ...task, id: index });
  });
  return groupedByPriority;
};

export const groupByTags = (tasks, tags) => {
  const groupedTags = {};
  tags.forEach((tag) => {
    groupedTags[tag] = [];
  });
  tasks.forEach((task, index) => {
    task?.tags?.forEach((tag) => {
      groupedTags[tag]?.push({ ...task, id: index });
    });
  });
  return groupedTags;
};

export const getDateDiff = (value, dateLogged) => {
  const startDate = new Date(dateLogged);
  const endDate = new Date(value);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const fullDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  return fullDays * 8;
};

export const getGroupedTasksByModule = (tasks) => {
  const groupedModules = {};
  const unCategorizedTasks = { module: [], group: {}, screen: {} };
  tasks?.forEach((task) => {
    const { moduleId, group, screenId } = task || {};
    if (!moduleId) {
      unCategorizedTasks?.module?.push(task);
    } else if (moduleId && !group) {
      if (!groupedModules[moduleId]) {
        groupedModules[moduleId] = {};
      }
      unCategorizedTasks.group[moduleId] = unCategorizedTasks.group[moduleId] || [];
      unCategorizedTasks.group[moduleId].push(task);
    } else if (moduleId && group && !screenId) {
      unCategorizedTasks.screen[group] = unCategorizedTasks.screen[group] || [];
      unCategorizedTasks.screen[group].push(task);
    } else if (moduleId && group && screenId) {
      if (!groupedModules[moduleId]) {
        groupedModules[moduleId] = {};
      }
      if (!groupedModules[moduleId][group]) {
        groupedModules[moduleId][group] = {};
      }
      if (!groupedModules[moduleId][group][screenId]) {
        groupedModules[moduleId][group][screenId] = [];
      }
      groupedModules[moduleId][group][screenId].push(task);
    }
  });
  return { groupedModules, unCategorizedTasks };
};

export const getMetaDataByModule = (groupedModules, unCategorizedTasks) => {
  const meta = {};
  const categoryMeta = {};
  Object.keys(groupedModules)?.forEach((module) => {
    let tasks = 0;
    let screens = 0;
    let subModules = Object.keys(groupedModules[module]).length;
    if (unCategorizedTasks.group[module]) {
      subModules = Object.keys(groupedModules[module]).length + 1;
      tasks = unCategorizedTasks.group[module].length;
    }
    meta[module] = {
      subModules,
      screens,
      tasks,
    };
    Object.keys(groupedModules[module])?.forEach((group) => {
      let categoryTasks = 0;
      Object.keys(groupedModules[module][group])?.forEach((screenId) => {
        tasks = groupedModules[module][group][screenId].length + tasks;
        screens += 1;
        categoryTasks = groupedModules[module][group][screenId].length + categoryTasks;
        categoryMeta[group] = {
          screens: Object.keys(groupedModules[module][group]).length || 0,
          tasks: categoryTasks || 0,
        };
        meta[module] = {
          subModules,
          screens,
          tasks,
        };
      });
    });
  });
  return { meta, categoryMeta };
};

export const getProjectByPath = (path) => {
  switch (path) {
    case PATH.BUGS_SUGGESTIONS:
      return 'roboteon-app-issues';
    case PATH.STS_US_ROBOTICS:
      return 'US_ROBOTICS';
    default:
      return null;
  }
};

/**
 * `userType` is stored at login: 'E' on the external domain; on the internal domain it is the
 * token's type, 'I' (internal) or 'B' (internal who can also open client projects).
 */
export const isUserBoth = () => localStorage.getItem('userType') === 'B';

/** External client users only. 'B' is internal, so it is not a client; unknown counts as one. */
export const isUserClient = () => {
  const userType = localStorage.getItem('userType');
  return userType !== 'I' && userType !== 'B';
};

/**
 * Billing invoices are only for the internal Manager who uploads them and the
 * external (`userType` 'E') user who raised that specific ticket — not other
 * internal roles (including 'B').
 */
export const canViewInvoice = (task) => {
  if (getUserRole()?.toLowerCase() === 'manager') return true;
  const userType = localStorage.getItem('userType');
  const loggedInUser = localStorage.getItem('loggedInUser');
  const requestor = task?.requestorName?.value ?? task?.requestorName;
  return userType === 'E' && !!requestor && requestor === loggedInUser;
};

export const getProjectId = (path, clientProject) => {
  const isClient = isUserClient();
  if (isClient) {
    return clientProject;
  }
  const project = getProjectByPath(path);
  return project || 'STS';
};

export function getNextDay(dateStr) {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}

export const extractProjectIds = (modules = []) => {
  if (!Array.isArray(modules)) return [];

  const projectsModule = modules.find(
    (mod) => mod.moduleName === 'Projects' || mod.path === 'Projects'
  );

  if (!projectsModule || !Array.isArray(projectsModule.menus)) return [];

  const projectIds = projectsModule.menus
    .map((menu) => {
      const path = menu.screenPath || '';

      // Only STS pages
      if (!path.includes('/support-tracking-system')) return null;

      try {
        const queryString = path.split('?')[1];
        if (!queryString) return { label: menu.caption, value: 'STS', type: 'normal' }; // default main STS

        const params = new URLSearchParams(queryString).get('projectId') || 'STS';
        const type = new URLSearchParams(queryString).get('type') || 'normal';
        return { label: menu.caption, value: params, type };
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);
  // ✅ Deduplicate
  return [...new Set(projectIds)];
};

// ID Regex
export const CommonIdRegex = /^[1-9a-zA-Z][0-9a-zA-Z\w]*$/;
export const commonNewIdRegex = /^[1-9a-zA-Z][0-9a-zA-Z-.+]*$/;
export const cartTypeRegex = /^[1-9a-zA-Z][0-9a-zA-Z-@.#&+\w!$%^*(){}[\]<>?/\\|`~:;'"=]*$/;
export const versionRegex = /v\d+\.\d+\.\d+/;
export const EMAIL_REGX = /\S+@\S+\.\S+/;
